import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Shield, ArrowLeft, User, Mail, Phone, Building, FileText, KeyRound, Loader2, Wrench } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ViewServiceAgreement } from '@/components/portal/ViewServiceAgreement';
import { MfaSettings } from '@/components/portal/MfaSettings';
import { Helmet } from '@/components/Helmet';
import { LogoShield } from '@/components/LogoShield';
import { CurrencyDock } from '@/components/portal/CurrencyDock';

export default function Account() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { checking } = useRequireMfa();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [runningBillingGraceCron, setRunningBillingGraceCron] = useState(false);
  const [billingGraceResult, setBillingGraceResult] = useState<string | null>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const getErrorMessage = (err: unknown) => {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    if (typeof err === 'object' && err && 'message' in err) {
      const msg = (err as { message?: unknown }).message;
      return typeof msg === 'string' ? msg : '';
    }
    return '';
  };

  // Check if user is admin
  const { data: isAdmin } = useQuery({
    queryKey: ['isAdmin', user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user!.id)
        .eq('role', 'admin')
        .maybeSingle();
      return !!data;
    },
    enabled: !!user,
  });


  const handleResetPassword = async () => {
    if (!user?.email) return;
    
    setIsResettingPassword(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/portal/auth`,
      });
      
      if (error) throw error;
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: unknown) {
      toast.error('Failed to send password reset email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user!.id)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setPhone(profile.phone || '');
      setCompanyName(profile.company_name || '');
    }
  }, [profile]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          phone: phone,
          company_name: companyName,
        })
        .eq('user_id', user!.id);

      if (error) throw error;
      toast.success('Account details updated successfully');
    } catch (err: unknown) {
      toast.error('Failed to update account details');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRunBillingGraceCron = async () => {
    setRunningBillingGraceCron(true);
    setBillingGraceResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('billing-grace-cron');
      if (error) throw error;
      const pausedCount = typeof data?.paused === 'number' ? data.paused : null;
      const success = data?.success === true;
      const message = pausedCount !== null
        ? `Billing grace check complete. Paused ${pausedCount} site(s).`
        : success
          ? 'Billing grace check complete.'
          : 'Billing grace check finished.';
      setBillingGraceResult(message);
      toast.success(message);
    } catch (err: unknown) {
      const errorMessage = getErrorMessage(err) || 'Unable to run billing grace check.';
      setBillingGraceResult(errorMessage);
      toast.error(errorMessage);
    } finally {
      setRunningBillingGraceCron(false);
    }
  };

  if (checking || loading || !user || profileLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Account Settings | ShadowScan Portal</title>
          <meta
            name="description"
            content="Update your ShadowScan portal profile, contact details, MFA preferences, and service agreement access to keep your security account current."
          />
        </Helmet>
        <div className="text-primary animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Account Settings | ShadowScan Portal</title>
        <meta
          name="description"
          content="Manage your ShadowScan portal account: edit contact info, company details, MFA settings, and access your security service agreement."
        />
      </Helmet>
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />
      
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/portal')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <LogoShield className="h-7 w-7" />
            <div className="flex flex-col leading-tight">
              <span className="text-sm font-mono text-muted-foreground">ShadowScan Portal</span>
              <span className="text-xl font-heading font-bold tracking-tight">Account Settings</span>
            </div>
          </div>
        </div>
      </header>

      <main className="relative z-10 container mx-auto px-4 py-8 max-w-2xl">
        <Card className="cyber-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Account Details
            </CardTitle>
            <CardDescription>
              Manage your account information and contact details.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={user.email || ''}
                  disabled
                  className="bg-muted/50 font-mono"
                />
                <p className="text-xs text-muted-foreground">Contact support to change your email address</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName" className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  Full Name
                </Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Smith"
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+61 400 000 000"
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  Company Name
                </Label>
                <Input
                  id="company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your Company Pty Ltd"
                  className="bg-muted/50"
                />
              </div>

              <div className="pt-4 border-t border-border">
                <Button type="submit" className="w-full" disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Security Section */}
        <Card className="cyber-card mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-primary" />
              Security
            </CardTitle>
            <CardDescription>
              Manage your account security settings.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div>
                <p className="font-medium">Reset Password</p>
                <p className="text-sm text-muted-foreground">Send a password reset link to your email</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleResetPassword}
                disabled={isResettingPassword}
                className="w-full sm:w-auto"
              >
                {isResettingPassword ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* MFA Settings */}
        <div className="mt-6">
          <MfaSettings
            userId={user.id}
            userEmail={user.email || ''}
            currentMethod={profile?.mfa_method || null}
            onMethodChange={() => queryClient.invalidateQueries({ queryKey: ['profile', user.id] })}
          />
        </div>

        {/* Legal Section */}
        <Card className="cyber-card mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Legal
            </CardTitle>
            <CardDescription>
              View your service agreement and terms.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-muted/30 rounded-lg border border-border/50">
              <div>
                <p className="font-medium leading-tight">Service Agreement</p>
                <p className="text-sm text-muted-foreground leading-tight">View the ShadowScan Security Services Agreement</p>
              </div>
              <div className="w-full sm:w-auto">
                <ViewServiceAgreement />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Tools - Only visible to admins */}
        {isAdmin && (
          <Card className="cyber-card mt-6 border-accent/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-accent">
                <Wrench className="h-5 w-5" />
                Admin Tools
              </CardTitle>
              <CardDescription>
                Administrative functions for system maintenance.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={runningBillingGraceCron}
                    >
                      {runningBillingGraceCron ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Running billing grace check...
                        </>
                      ) : (
                        'Run billing grace check'
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Run billing grace check?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will evaluate billing grace periods and pause auto-updates for domains past the 7-day grace window.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={runningBillingGraceCron}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleRunBillingGraceCron} disabled={runningBillingGraceCron}>
                        Run now
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={async () => {
                    Sentry.captureException(new Error('Sentry test error'));
                    const flushed = await Sentry.flush(2000);
                    if (flushed) {
                      toast.success('Sentry test event sent.');
                    } else {
                      toast.error('Sentry test event not confirmed. Check DSN and network.');
                    }
                  }}
                >
                  Throw Sentry Error
                </Button>
                {billingGraceResult && (
                  <span className="text-xs text-muted-foreground">{billingGraceResult}</span>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      <CurrencyDock />
    </div>
  );
}
